import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Download, 
  Share2, 
  Printer, 
  X,
  Hash,
  User,
  Calendar,
  Package
} from 'lucide-react';
import { toPng } from 'html-to-image';
import { useTranslation } from 'react-i18next';
import Lottie from "lottie-react";
import successAnimation from "../../../../public/lotties/succcess.json";
import failedAnimation from "../../../../public/lotties/error.json";

const OrderReceipt = ({ order, onClose, open }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [renderError, setRenderError] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const receiptRef = useRef(null);
  const isMounted = useRef(true);
  const abortControllerRef = useRef(null);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return { date: 'N/A', time: 'N/A' };
    try {
      const date = new Date(dateString);
      return {
        date: date.toLocaleDateString(t('common.locale') || 'en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        }),
        time: date.toLocaleTimeString(t('common.locale') || 'en-US', { 
          hour: '2-digit', 
          minute: '2-digit' 
        })
      };
    } catch (error) {
      return { date: 'N/A', time: 'N/A' };
    }
  }, [t]);

  const { date, time } = order?.createdAt ? formatDate(order.createdAt) : { date: 'N/A', time: 'N/A' };

  const getStatusColor = useCallback((status) => {
    switch (status?.toLowerCase()) {
      case 'succeeded':
      case 'success':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-secondary-100 text-secondary-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }, []);

  const getAnimation = useCallback(() => {
    if (!order) return null;
    if (order?.status === 'succeeded' || order?.status === 'success') {
      return successAnimation;
    } else if (order?.status === 'failed') {
      return failedAnimation;
    }
    return null;
  }, [order]);

  const cleanupOperations = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    
    return () => {
      isMounted.current = false;
      cleanupOperations();
    };
  }, [cleanupOperations]);

  useEffect(() => {
    if (open) {
      const checkRef = () => {
        if (isMounted.current) {
          setIsReady(!!receiptRef.current);
        }
      };
      
      requestAnimationFrame(() => {
        requestAnimationFrame(checkRef);
      });
    } else {
      setIsReady(false);
      cleanupOperations();
    }
  }, [open, cleanupOperations]);

  useEffect(() => {
    if (!open) {
      setIsSharing(false);
      setIsDownloading(false);
      setIsPrinting(false);
      setRenderError(null);
      setCopied(false);
    }
  }, [open]);

  const captureReceipt = useCallback(async () => {
    if (!receiptRef.current) return null;
    abortControllerRef.current = new AbortController();
    
    try {
      const element = receiptRef.current;
      await new Promise(resolve => requestAnimationFrame(resolve));
      
      if (abortControllerRef.current.signal.aborted) {
        return null;
      }
      
      const dataUrl = await toPng(element, {
        quality: 1.0,
        pixelRatio: 2,
        backgroundColor: '#ffffff',
        cacheBust: true,
        filter: (node) => {
          if (node.tagName === 'SCRIPT' || node.tagName === 'IFRAME') {
            return false;
          }
          if (node.className && typeof node.className === 'string' && 
              node.className.includes('stripe')) {
            return false;
          }
          return true;
        }
      });
      
      return dataUrl;
    } catch (error) {
      console.error('Error capturing receipt:', error);
      return null;
    } finally {
      abortControllerRef.current = null;
    }
  }, []);

  const downloadAsImage = useCallback(async () => {
    if (!receiptRef.current || isDownloading || !isMounted.current) return;
    
    cleanupOperations();
    
    setIsDownloading(true);
    setRenderError(null);
    
    try {
      const dataUrl = await captureReceipt();
      
      if (!dataUrl || !isMounted.current) {
        throw new Error('Failed to capture receipt');
      }
      
      const link = document.createElement('a');
      link.download = `order-receipt-${order?.txnNumber || 'order'}-${Date.now()}.png`;
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);
      
    } catch (error) {
      console.error('Error downloading image:', error);
      if (isMounted.current) {
        setRenderError(t('orderReceipt.downloadError') || 'Failed to download. Please try again.');
      }
    } finally {
      if (isMounted.current) {
        setIsDownloading(false);
      }
    }
  }, [order?.txnNumber, isDownloading, captureReceipt, cleanupOperations, t]);

  const shareReceipt = useCallback(async () => {
    if (!receiptRef.current || isSharing || !isMounted.current) return;
    
    cleanupOperations();
    
    setIsSharing(true);
    setRenderError(null);
    
    try {
      const dataUrl = await captureReceipt();
      
      if (!dataUrl || !isMounted.current) {
        throw new Error('Failed to capture receipt for sharing');
      }
      
      const blob = await fetch(dataUrl).then(res => res.blob());
      
      if (navigator.share && navigator.canShare) {
        try {
          const file = new File([blob], `order-receipt-${order?.txnNumber || 'order'}.png`, { 
            type: 'image/png' 
          });
          
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              title: t('orderReceipt.shareTitle') || 'Order Receipt',
              text: t('orderReceipt.shareText', { 
                amount: parseFloat(order?.totalAmount || order?.amount || 0).toFixed(2), 
                receiver: order?.receiver 
              }) || `Order receipt for ${order?.receiver}`,
              files: [file]
            });
          } else {
            await downloadAsImage();
          }
        } catch (shareError) {
          console.error('Share error:', shareError);
          if (shareError.name !== 'AbortError' && isMounted.current) {
            await downloadAsImage();
          }
        }
      } else {
        await downloadAsImage();
      }
    } catch (error) {
      console.error('Error sharing receipt:', error);
      if (isMounted.current) {
        setRenderError(t('orderReceipt.shareError') || 'Failed to share. Downloading instead...');
        await downloadAsImage();
      }
    } finally {
      if (isMounted.current) {
        setIsSharing(false);
      }
    }
  }, [order, isSharing, captureReceipt, downloadAsImage, cleanupOperations, t]);

  const printReceipt = useCallback(async () => {
    if (!receiptRef.current || isPrinting || !isMounted.current) return;
    
    cleanupOperations();
    
    setIsPrinting(true);
    setRenderError(null);
    
    try {
      const receiptElement = receiptRef.current;
      const clonedContent = receiptElement.cloneNode(true);
      
      const scripts = clonedContent.querySelectorAll('script');
      scripts.forEach(script => script.remove());
      
      const iframes = clonedContent.querySelectorAll('iframe');
      iframes.forEach(iframe => iframe.remove());
      
      const canvases = clonedContent.querySelectorAll('canvas');
      canvases.forEach(canvas => canvas.remove());
      
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        throw new Error(t('orderReceipt.popupBlocked') || 'Popup blocker prevented printing');
      }
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${t('orderReceipt.printTitle') || 'Order Receipt'} - ${order?.txnNumber || 'Order'}</title>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <style>
              /* Reset and base styles */
              * { 
                margin: 0; 
                padding: 0; 
                box-sizing: border-box; 
              }
              
              body { 
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
                padding: 20px;
                background: white;
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: 100vh;
              }
              
              .receipt-container {
                max-width: 500px;
                width: 100%;
                background: white;
                padding: 24px;
                border-radius: 8px;
                box-shadow: 0 2px 4px rgba(0,0,0,0.1);
              }
              
              /* Utility classes */
              .text-center { text-align: center; }
              .mb-6 { margin-bottom: 24px; }
              .py-2 { padding: 8px 0; }
              .px-4 { padding: 0 16px; }
              .rounded-full { border-radius: 9999px; }
              .bg-green-100 { background-color: #dcfce7; }
              .bg-red-100 { background-color: #fee2e2; }
              .bg-yellow-100 { background-color: #fef9c3; }
              .bg-secondary-100 { background-color: #dbeafe; }
              .bg-gray-100 { background-color: #f3f4f6; }
              .text-green-800 { color: #166534; }
              .text-red-800 { color: #991b1b; }
              .text-yellow-800 { color: #854d0e; }
              .text-secondary-800 { color: #1e40af; }
              .text-gray-800 { color: #1f2937; }
              .flex { display: flex; }
              .justify-between { justify-content: space-between; }
              .items-center { align-items: center; }
              .space-y-4 > * + * { margin-top: 16px; }
              .border-t { border-top: 1px solid #e5e7eb; }
              .pt-4 { padding-top: 16px; }
              .mt-4 { margin-top: 16px; }
              .font-mono { font-family: 'Courier New', monospace; }
              .font-bold { font-weight: bold; }
              .font-semibold { font-weight: 600; }
              .text-gray-600 { color: #4b5563; }
              .text-red-600 { color: #dc2626; }
              .text-sm { font-size: 14px; }
              .text-lg { font-size: 18px; }
              .text-xl { font-size: 20px; }
              .mr-2 { margin-right: 8px; }
              .capitalize { text-transform: capitalize; }
              .break-words { word-wrap: break-word; }
              img { 
                max-width: 100%; 
                height: auto; 
              }
              
              @media print {
                body { 
                  padding: 0; 
                }
                .receipt-container { 
                  box-shadow: none; 
                  padding: 16px;
                }
              }
            </style>
          </head>
          <body>
            <div class="receipt-container">
              ${clonedContent.innerHTML}
            </div>
            <script>
              // Auto-trigger print when ready
              (function() {
                let attempts = 0;
                const maxAttempts = 10;
                
                function tryPrint() {
                  if (document.readyState === 'complete') {
                    window.print();
                    setTimeout(function() { 
                      window.close(); 
                    }, 500);
                  } else if (attempts < maxAttempts) {
                    attempts++;
                    setTimeout(tryPrint, 100);
                  } else {
                    // Force print after timeout
                    window.print();
                    setTimeout(function() { 
                      window.close(); 
                    }, 500);
                  }
                }
                
                tryPrint();
              })();
            <\/script>
          </body>
        </html>
      `);
      
      printWindow.document.close();
    } catch (error) {
      console.error('Error printing receipt:', error);
      if (isMounted.current) {
        setRenderError(t('orderReceipt.printError') || 'Failed to print. Please try using download instead.');
      }
    } finally {
      if (isMounted.current) {
        setIsPrinting(false);
      }
    }
  }, [order?.txnNumber, isPrinting, cleanupOperations, t]);

  if (!open || !order) return null;

  const animationData = getAnimation();
  const areButtonsEnabled = open && isReady;

  return (
    <div className="fixed inset-0 bg-black/30 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-[500px] max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-gray-800">
            {t('orderReceipt.title') || 'Order Receipt'}
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-50"
            disabled={isSharing || isDownloading || isPrinting}
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>

        {renderError && (
          <div className="mx-4 mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-700">{renderError}</p>
          </div>
        )}

        <div ref={receiptRef} className="p-6 bg-white rounded-lg">
          <div className="text-center mb-6">
            <div className="flex justify-center mb-4">
              <img
                className="w-auto h-[40px]"
                src="/images/afh-logo.png"
                alt={t('common.logoAlt') || 'Logo'}
                width={140}
                height={40}
                loading="lazy"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
            
            {animationData && (
              <div className="flex justify-center mb-4">
                <Lottie 
                  animationData={animationData} 
                  loop={false}
                  style={{ width: 200, height: 200 }}
                />
              </div>
            )}
          </div>

          <div className={`text-center py-2 px-4 rounded-full mb-6 font-semibold inline-block w-full mx-auto ${getStatusColor(order.status)}`}>
            {t(`orderReceipt.status.${order.status?.toLowerCase() || 'pending'}`)?.toUpperCase() || order.status?.toUpperCase()}
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center text-gray-600">
                <Hash size={15} className="mr-2" />
                <span className="text-[15px]">{t('orderReceipt.transactionId') || 'Transaction ID'}</span>
              </div>
              <span className="font-mono text-[15px] text-gray-800 break-words max-w-[200px] text-right">
                {order.txnNumber}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <div className="flex items-center text-gray-600">
                <Calendar size={16} className="mr-2" />
                <span className="text-[15px]">{t('orderReceipt.dateTime') || 'Date & Time'}</span>
              </div>
              <div className="text-right">
                <div className="text-gray-800 text-[15px]">{date}</div>
                <div className="text-sm text-gray-600">{time}</div>
              </div>
            </div>

            <div className="flex justify-between items-center">
              <div className="flex items-center text-gray-600">
                <User size={16} className="mr-2" />
                <span className="text-[15px]">{t('orderReceipt.receiver') || 'Receiver'}</span>
              </div>
              <span className="font-medium text-[15px] text-gray-800 break-words max-w-[200px] text-right">
                {order.receiver}
              </span>
            </div>

          

            {order.type && (
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-[15px]">{t('orderReceipt.type') || 'Type'}</span>
                <span className="font-medium text-[15px] text-gray-800 capitalize">
                  {order.type}
                </span>
              </div>
            )}

            {order.productDetails?.description && (
              <div className="flex justify-between items-start">
                <span className="text-gray-600 text-[15px]">{t('orderReceipt.description') || 'Description'}</span>
                <span className="font-medium text-[15px] text-gray-800 text-right max-w-[200px] break-words">
                  {order.productDetails.description}
                </span>
              </div>
            )}

            {order.meta?.error && (
              <div className="mt-2 p-3 bg-red-50 rounded-lg">
                <p className="text-sm text-red-600 break-words">{order.meta.error}</p>
              </div>
            )}

            <div className="border-t pt-4 mt-4">
              <div className="flex justify-between items-center text-lg font-bold">
                <span className="text-gray-800">{t('orderReceipt.amount') || 'Amount'}</span>
                <span className="text-red-600">
                  {parseFloat(order.totalAmount || order.amount || 0).toFixed(2)} {order.currency || t('common.currency') || 'AF'}
                </span>
              </div>
            </div>
          </div>

          <div className="text-center text-sm text-gray-500 border-t pt-4 mt-4">
            <p>{t('orderReceipt.thankYou') || 'Thank you for using YES'}</p>
            <p>{t('orderReceipt.support') || 'For support'}: support@yes-charge.com</p>
          </div>
        </div>

        <div className="flex flex-wrap justify-center gap-3 p-4 border-t bg-white sticky bottom-0">
          <button
            onClick={downloadAsImage}
            disabled={!areButtonsEnabled || isDownloading || isSharing || isPrinting}
            className={`flex items-center px-4 py-2 rounded-md transition-colors ${
              !areButtonsEnabled || isDownloading || isSharing || isPrinting
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                : 'bg-gray-600 text-white hover:bg-gray-700'
            }`}
          >
            <Download size={16} className="mr-2" />
            {isDownloading ? (t('common.downloading') || 'Downloading...') : (t('common.download') || 'Download')}
          </button>
          
          <button
            onClick={printReceipt}
            disabled={!areButtonsEnabled || isPrinting || isDownloading || isSharing}
            className={`flex items-center px-4 py-2 rounded-md transition-colors ${
              !areButtonsEnabled || isPrinting || isDownloading || isSharing
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                : 'bg-primary text-white hover:bg-[#df2323]'
            }`}
          >
            <Printer size={16} className="mr-2" />
            {isPrinting ? (t('common.printing') || 'Printing...') : (t('common.print') || 'Print')}
          </button>

          <button
            onClick={shareReceipt}
            disabled={!areButtonsEnabled || isSharing || isDownloading || isPrinting}
            className={`flex items-center px-4 py-2 rounded-md transition-colors ${
              !areButtonsEnabled || isSharing || isDownloading || isPrinting
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                : 'bg-secondary-600 text-white hover:bg-secondary-700'
            }`}
          >
            <Share2 size={16} className="mr-2" />
            {isSharing ? (t('common.sharing') || 'Sharing...') : (t('common.share') || 'Share')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderReceipt;